<template>
  <div class="row my-5">
    <div class="col-12 col-md-4 mb-3" v-for="stat in statsList" :key="stat.label">
      <Stat-card :label="stat.label" :value="stat.value" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';

import StatCard from "@/components/views/stastistics/StatCard.vue";

/**
 * Reactive object containing statistical data.
 *
 * @property {number} users - The total number of users.
 * @property {number} links - The total number of links.
 * @property {number} visitedLinks - The total number of visited links.
 */
const stats = ref({
  users: 0,
  links: 0,
  visitedLinks: 0,
});
const loading = ref(false);
const error = ref(null);

const apiUrl = process.env.VUE_APP_API_URL;

/**
 * Fetches statistics including counts for users, links, and visited links from the API.
 *
 * @param {string} token - The authentication token to authorize API requests.
 * @return {Promise<Object>} A promise that resolves to an object containing the counts for users, links, and visited links:
 * - users: Total number of users.
 * - links: Total number of links.
 * - visitedLinks: Total number of visited links.
 */
async function fetchStatistics(token) {
  const headers = { 'Authorization': `Bearer ${token}` };
  const [usersRes, linksRes, visitedLinksRes] = await Promise.all([
    axios.get(`${apiUrl}/count/all/users`, { headers }),
    axios.get(`${apiUrl}/count/all/links`, { headers }),
    axios.get(`${apiUrl}/count/all/linksVisited`, { headers }),
  ]);
  return {
    users: usersRes.data.count,
    links: linksRes.data.count,
    visitedLinks: visitedLinksRes.data.count,
  };
}

onMounted(/**
 * Asynchronously fetches and updates statistics data, while managing loading and error states.
 *
 * Retrieves a token from sessionStorage and utilizes it to fetch statistics.
 * During the operation, a loading state is activated, and any errors encountered are handled and captured.
 *
 * Variables:
 * - `loading.value`: Indicates whether the fetch operation is in progress.
 * - `stats.value`: Stores the fetched statistics data.
 * - `error.value`: Captures any error message that occurs during the fetch operation.
 *
 * Operations:
 * - Retrieves a session token from sessionStorage.
 * - Invokes `fetchStatistics()` to fetch statistics data using the token.
 * - Handles any errors that occur during the fetch operation and updates the error state if needed.
 * - Ensures the loading state is reset after the operation completes, regardless of success or error.
 */
async () => {
  loading.value = true;
  const token = sessionStorage.getItem('token');
  try {
    stats.value = await fetchStatistics(token);
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
});

/**
 * Reactive computed property that generates a list of statistics objects.
 * Each object in the list contains a label and its corresponding value from
 * the provided `stats` data source.
 *
 * The list includes:
 * - The total number of users.
 * - The total number of short links.
 * - The total number of visited links.
 *
 * @constant {Object[]} statsList
 * @property {string} label - The label describing the statistic.
 * @property {number} value - The numeric value associated with the statistic, derived from `stats.value`.
 */
const statsList = computed(() => [
  { label: 'Total users', value: stats.value.users },
  { label: 'Total short links', value: stats.value.links },
  { label: 'Visited links ', value: stats.value.visitedLinks },
]);
</script>

<style scoped>
.card-title {
  font-size: 1.2rem;
  font-weight: bold;
}
</style>
